import { Dispatch, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';

import type {
  AssessmentAction,
  AssessmentReducer,
  AssessmentState,
} from '~/lib/constants/patients.constants';
import {
  CreateAssessmentIntent,
  UpdateAssessmentIntent,
} from '~/lib/constants/patients.constants';
import { SurveyName } from '~/lib/constants/survey/SurveyName';
import { SurveyResponseSource } from '~/lib/constants/survey/SurveyResponseSource';
import { SurveyResponseStatus } from '~/lib/constants/survey/SurveyResponseStatus';
import { ClinicianAssessmentsSurveyCards } from '~/lib/util/survey.util';
import { onContinueSurvey as continueSurveyUtil } from '~/lib/util/surveyActions.util';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import {
  PortalComponent,
  useComponentPortalContext,
} from '~/lib/contexts/ComponentPortalContext';
import {
  useCreateAssessmentMutation,
  useGetAssessmentsListQuery,
  useGetSurveysQuery,
  useUpdateAssessmentMutation,
} from '~/store/services/assessments';
import {
  useGetPatientContactsQuery,
  useGetPatientQuery,
} from '~/store/services/patients';
import { ApiCommand } from '~/lib/Api';
import { useApi } from '~/lib/hooks';
import urlConstants from '~/lib/constants/url.constants';

const assessmentReducer = (
  state: AssessmentState,
  action: AssessmentAction,
): AssessmentState => {
  const { field, data } = action;
  if (field === 'SET') return { ...state, ...data };
  return { ...state, [field]: data };
};

const INITIAL_STATE: AssessmentState = {
  assessmentList: [],
  createAssessmentIntent: null,
  emailSentData: null,
  isAssessmentListError: false,
  isAssessmentListLoading: false,
  selectedAssessment: null,
  selectedSurveyResults: null,
  updateAssessmentIntent: null,
};

export interface AssessmentActionsResult {
  assessmentList: SurveyResponse[];
  isAssessmentError: boolean;
  isAssessmentLoading: boolean;
  onContinueSurvey: (externalId: string) => void;
  onSendEmail: (assessment: Partial<SurveyResponse>) => void;
  onUpdateAssessmentStatus: (
    status: SurveyResponseStatus,
    toUserEmail: string,
    responseExternalId: string,
  ) => void;
  selectedSurveyResults: Partial<SurveyResponse> | null;
  surveys: Survey[];
  updateAssessmentState: Dispatch<AssessmentAction>;
}

/**
 * Assessment state management extracted from PatientProfile for use on individual tab routes.
 * Replaces prop-drilling via PatientTabProps. Promote to a context provider when more
 * consumers need it (e.g. PatientLayout-level actions).
 */
export const useAssessmentActions = (
  patientId: string,
): AssessmentActionsResult => {
  const { currentUser } = useGlobalContext();
  const {
    firstName: currentUserFirstName,
    lastName: currentUserLastName,
    userId: currentUserId,
  } = currentUser;

  const { addEditSurveyRespondentAccordion, setComponentVisibility } =
    useComponentPortalContext();

  const [assessmentState, updateAssessmentState] =
    useReducer<AssessmentReducer>(assessmentReducer, INITIAL_STATE);
  const {
    createAssessmentIntent,
    emailSentData,
    selectedAssessment,
    selectedSurveyResults,
    updateAssessmentIntent,
  } = assessmentState;

  const {
    data: assessmentsList,
    isError: isAssessmentError,
    isLoading: isAssessmentLoading,
  } = useGetAssessmentsListQuery({ patientId }, { skip: !patientId });

  const { data: getSurveysResponse } = useGetSurveysQuery({
    filterBy: { surveyName: SurveyName.ADMISSIONS_INTAKE },
  });

  const [createAssessment, { data: createAssessmentData, isLoading: isCreateAssessmentLoading }] =
    useCreateAssessmentMutation();

  const [updateAssessment, { data: updateAssessmentData, error: updateAssessmentError, isLoading: isUpdateAssessmentLoading }] =
    useUpdateAssessmentMutation();

  const { loading: isSendEmailProcessing, sendRequest: sendEmail, statusCode: sendEmailStatusCode } = useApi();

  const onSendEmail = useCallback(
    ({
      responseExternalId,
      status,
      surveyName,
      toUserEmail,
    }: Partial<SurveyResponse>): void => {
      sendEmail({
        command: ApiCommand.POST,
        options: {
          requestedByUserFirstName: currentUserFirstName,
          requestedByUserLastName: currentUserLastName,
          responseExternalId,
        },
        url: urlConstants.assessments.sendEmail,
      });
      updateAssessmentState({
        data: { status, surveyName, toUserEmail },
        field: 'emailSentData',
      });
    },
    [currentUserFirstName, currentUserLastName, sendEmail],
  );

  const onUpdateAssessmentStatus = useCallback(
    (
      status: SurveyResponseStatus,
      toUserEmail: string,
      responseExternalId: string,
    ) =>
      updateAssessment({
        responseExternalId,
        assessment: {
          status,
          submittedSource: SurveyResponseSource.WEB,
          submittedUserFirstName: currentUserFirstName,
          submittedUserId: currentUserId,
          submittedUserLastName: currentUserLastName,
          toUserEmail,
          userTimezoneId: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      }),
    [currentUserFirstName, currentUserId, currentUserLastName, updateAssessment],
  );

  const assessmentsRef = useRef(assessmentsList?.assessments);
  assessmentsRef.current = assessmentsList?.assessments;

  const onContinueSurvey = useCallback(
    (externalId: string) => {
      continueSurveyUtil(
        externalId,
        assessmentsRef.current ?? [],
        setComponentVisibility,
      );
    },
    [setComponentVisibility],
  );

  // When a clinician assessment is selected, create it immediately (no respondent needed)
  useEffect(() => {
    if (
      selectedAssessment &&
      ClinicianAssessmentsSurveyCards.includes(selectedAssessment?.surveyName)
    ) {
      createAssessment({
        assigneeExternalId: selectedAssessment.assigneeExternalId,
        status: selectedAssessment.status as SurveyResponseStatus,
        submittedSource: SurveyResponseSource.WEB,
        submittedUserFirstName: currentUserFirstName,
        submittedUserId: currentUserId,
        submittedUserLastName: currentUserLastName,
        surveyName: selectedAssessment.surveyName as SurveyName,
        toUserEmail: selectedAssessment.toUserEmail,
        toUserFirstName: selectedAssessment.toUserFirstName,
        toUserLastName: selectedAssessment.toUserLastName,
        toUserPhone: selectedAssessment.toUserPhone,
        userId: patientId,
      });
    }
  }, [!!selectedAssessment]);

  // After creation with BEGIN_NOW intent, open the survey accordion
  useEffect(() => {
    const { responseExternalId } = createAssessmentData ?? {};
    if (responseExternalId) {
      if (createAssessmentIntent === CreateAssessmentIntent.BEGIN_NOW) {
        const match = assessmentsList?.assessments?.find(
          ({ responseExternalId: extId }: SurveyResponse) =>
            extId === responseExternalId,
        );
        if (match) {
          setComponentVisibility({
            component: PortalComponent.SURVEY_ACCORDION,
            data: match,
            isExpanded: true,
            isVisible: true,
          });
          updateAssessmentState({
            data: null,
            field: 'createAssessmentIntent',
          });
          updateAssessmentState({ data: null, field: 'selectedAssessment' });
        }
      } else if (
        createAssessmentIntent === CreateAssessmentIntent.SEND_INVITATION
      ) {
        const data = {
          ...selectedAssessment,
          ...createAssessmentData,
        };
        onUpdateAssessmentStatus(
          SurveyResponseStatus.REQUESTED,
          data.toUserEmail,
          data.responseExternalId,
        );
        updateAssessmentState({
          data: {
            createAssessmentIntent: null,
            emailSentData: data,
            updateAssessmentIntent: UpdateAssessmentIntent.SEND_INVITATION,
          },
          field: 'SET',
        });
      }
    }
  }, [createAssessmentData?.responseExternalId, assessmentsList]);

  const didSendEmail =
    updateAssessmentData &&
    !updateAssessmentError &&
    updateAssessmentIntent === UpdateAssessmentIntent.SEND_INVITATION;

  const showEmailSentToast = useCallback((statusCodeOverride?: number) => {
    const { status, surveyName, toUserEmail } = emailSentData ?? {};
    const statusPart =
      status === SurveyResponseStatus.NEW ? 'invitation' : 'reminder';
    const toEmailPart = toUserEmail ? ` to ${toUserEmail}` : '';

    let message = `${surveyName} survey ${statusPart} email sent${toEmailPart}`;
    let title = `${surveyName} survey email sent`;

    const effectiveStatusCode = statusCodeOverride ?? sendEmailStatusCode;
    if (effectiveStatusCode && effectiveStatusCode !== 200) {
      message = `Unable to send ${surveyName} survey ${statusPart} email${toEmailPart}`;
      title = `${surveyName} survey email failure`;
    }

    setComponentVisibility({
      component: PortalComponent.TOAST_ACCORDION,
      data: { message, title },
      isExpanded: true,
      isVisible: true,
    });
  }, [emailSentData, sendEmailStatusCode, setComponentVisibility]);

  useEffect(() => {
    if (didSendEmail) {
      updateAssessmentState({
        data: {
          createAssessmentIntent: null,
          selectedAssessment: null,
          updateAssessmentIntent: null,
        },
        field: 'SET',
      });
      showEmailSentToast(200);
    }
  }, [didSendEmail]);

  useEffect(() => {
    if (sendEmailStatusCode) showEmailSentToast();
  }, [sendEmailStatusCode]);

  // --- Survey respondent modal (pencil icon) support ---
  const { data: patient } = useGetPatientQuery(
    { patientId },
    { skip: !patientId },
  );

  const { data: patientContactList } = useGetPatientContactsQuery(patientId, {
    skip: !patientId,
  });

  const {
    data: updateRespondentData,
    loading: isUpdateRespondentProcessing,
    sendRequest: updateAssessmentRespondent,
  } = useApi();

  const { toUserEmail, toUserFirstName, toUserLastName, toUserPhone } =
    selectedAssessment ?? {};

  const hasEquipAccount = useMemo(() => {
    if (patient?.isAccountActive && patient?.hasAuthId) return true;
    const contacts = patientContactList ?? [];
    return contacts.some(
      (contact) =>
        contact.isProxy === true &&
        contact.hasLoggedIn === true &&
        contact.hasSetPassword === true,
    );
  }, [patient, patientContactList]);

  const handleRespondentConfirmClick = useCallback(
    ({
      assigneeExternalId,
      toUserEmail: email,
      toUserFirstName: firstName,
      toUserLastName: lastName,
      toUserPhone: phone,
    }: {
      assigneeExternalId?: string;
      toUserEmail: string;
      toUserFirstName: string;
      toUserLastName: string;
      toUserPhone: string;
    }) => {
      const updated = {
        ...selectedAssessment,
        assigneeExternalId,
        toUserEmail: email,
        toUserFirstName: firstName,
        toUserLastName: lastName,
        toUserPhone: phone,
      };

      if (createAssessmentIntent !== null) {
        createAssessment({
          assigneeExternalId: updated.assigneeExternalId,
          status: updated.status as SurveyResponseStatus,
          submittedSource: SurveyResponseSource.WEB,
          submittedUserFirstName: currentUserFirstName,
          submittedUserId: currentUserId,
          submittedUserLastName: currentUserLastName,
          surveyName: updated.surveyName as SurveyName,
          toUserEmail: email,
          toUserFirstName: firstName,
          toUserLastName: lastName,
          toUserPhone: phone,
          userId: patientId,
        });
      }

      if (updateAssessmentIntent === UpdateAssessmentIntent.UPDATE_RESPONDENT) {
        updateAssessmentRespondent({
          command: ApiCommand.PATCH,
          options: {
            toUserEmail: email,
            toUserFirstName: firstName,
            toUserLastName: lastName,
            toUserPhone: phone,
          },
          url: urlConstants.assessments.updateAssessmentRespondent(
            updated.responseExternalId,
          ),
        });
      }

      if (updateAssessmentIntent === UpdateAssessmentIntent.SEND_INVITATION) {
        onSendEmail(updated);
      }

      updateAssessmentState({ data: updated, field: 'selectedAssessment' });
    },
    [
      createAssessmentIntent,
      createAssessment,
      currentUserFirstName,
      currentUserId,
      currentUserLastName,
      patientId,
      selectedAssessment,
      updateAssessmentIntent,
      updateAssessmentRespondent,
      onSendEmail,
    ],
  );

  const handleRespondentClose = useCallback(() => {
    updateAssessmentState({
      data: {
        createAssessmentIntent: null,
        selectedAssessment: null,
        updateAssessmentIntent: null,
      },
      field: 'SET',
    });
  }, []);

  const isAssessmentRequestProcessing =
    isCreateAssessmentLoading ||
    isSendEmailProcessing ||
    isUpdateAssessmentLoading ||
    isUpdateRespondentProcessing;

  useEffect(() => {
    if (
      selectedAssessment &&
      !ClinicianAssessmentsSurveyCards.includes(selectedAssessment?.surveyName)
    ) {
      setComponentVisibility({
        component: PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
        data: {
          hasEquipAccount,
          isLoading: isAssessmentRequestProcessing,
          onCancel: handleRespondentClose,
          onConfirm: handleRespondentConfirmClick,
          toUserEmail: toUserEmail ?? '',
          toUserFirstName: toUserFirstName ?? '',
          toUserLastName: toUserLastName ?? '',
          toUserPhone: toUserPhone ?? '',
        },
        hasUnsavedChanges: false,
        isExpanded: true,
        isVisible: true,
      });
    } else if (!selectedAssessment) {
      setComponentVisibility({
        component: PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
        data: {},
        hasUnsavedChanges: false,
        isExpanded: false,
        isVisible: false,
      });
    }
  }, [!!selectedAssessment]);

  useEffect(() => {
    if (selectedAssessment) {
      setComponentVisibility({
        component: PortalComponent.ADD_EDIT_SURVEY_RESPONDENT_ACCORDION,
        data: {
          hasEquipAccount,
          isLoading: isAssessmentRequestProcessing,
          onCancel: handleRespondentClose,
          onConfirm: handleRespondentConfirmClick,
          toUserEmail: toUserEmail ?? '',
          toUserFirstName: toUserFirstName ?? '',
          toUserLastName: toUserLastName ?? '',
          toUserPhone: toUserPhone ?? '',
        },
      });
    }
  }, [isAssessmentRequestProcessing]);

  useEffect(() => {
    if (!addEditSurveyRespondentAccordion?.isVisible) {
      updateAssessmentState({ data: null, field: 'selectedAssessment' });
    }
  }, [addEditSurveyRespondentAccordion?.isVisible]);

  useEffect(() => {
    if (updateRespondentData) {
      handleRespondentClose();
    }
  }, [updateRespondentData]);

  return {
    assessmentList: assessmentsList?.assessments ?? [],
    isAssessmentError,
    isAssessmentLoading,
    onContinueSurvey,
    onSendEmail,
    onUpdateAssessmentStatus,
    selectedSurveyResults,
    surveys: getSurveysResponse?.surveys ?? [],
    updateAssessmentState,
  };
};
